import React, { useContext, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { BookingContext } from '../../context/BookingContext';
import { firestore } from '../../firebase/config';
import PractitionerSelection from './PractitionerSelection';
import ServiceSelection from './ServiceSelection';
import Calendar from './Calendar';
import TimeSlots from './TimeSlots';
import ClientForm from './ClientForm';
import Confirmation from './Confirmation';
import Sidebar from '../Layout/Sidebar';
import './Booking.css';

const Booking = () => {
  // Récupérer les paramètres d'URL de manière sécurisée
  const params = useParams();
  const practitionerId = params?.practitionerId || null;
  const serviceId = params?.serviceId || null;
  
  const location = useLocation();
  const { 
    currentStep, 
    setCurrentStep,
    bookingData, 
    updateBookingData, 
    setSelectedPractitioner,
    setSelectedService 
  } = useContext(BookingContext);

  // Logs de débogage améliorés
  useEffect(() => {
    console.log("=== État de la réservation ===");
    console.log("Paramètres URL:", { practitionerId, serviceId });
    console.log("État actuel de bookingData:", bookingData);
    console.log("Étape actuelle:", currentStep);
    console.log("Mode de consultation:", bookingData.consultationMode);
    console.log("===============================");
  }, [practitionerId, serviceId, bookingData, currentStep]);

  // Charger le praticien et le service à partir de l'URL si présents
  useEffect(() => {
    const loadFromUrl = async () => {
      // Vérifier si un practitionerId est présent dans l'URL ou les query params
      const urlPractitionerId = practitionerId || new URLSearchParams(location.search).get('practitioner');
      console.log("Chargement pour le praticien:", urlPractitionerId);
      
      // Ne charger le praticien que si aucun n'est déjà sélectionné
      if (urlPractitionerId && !bookingData.practitioner) {
        try {
          // Récupérer les données du praticien depuis Firestore
          const practitionerDoc = await firestore
            .collection('practitioners')
            .doc(urlPractitionerId)
            .get();
            
          if (practitionerDoc.exists) {
            const practitionerData = practitionerDoc.data();
            console.log("Praticien trouvé:", practitionerData);
            
            // Vérifier si le praticien est actif
            if (practitionerData.isActive !== false) {
              // Définir le praticien présélectionné
              setSelectedPractitioner({
                id: urlPractitionerId,
                name: practitionerData.name || 'Praticien sans nom',
                title: practitionerData.title || '',
                specialties: practitionerData.specialties || [],
                profileImage: practitionerData.profileImage || '/images/default-avatar.png'
              });
              
              // Passer directement à l'étape de sélection du service
              setCurrentStep(2);
              
              // Si un serviceId est présent dans l'URL, charger également le service
              if (serviceId) {
                try {
                  console.log("Chargement du service:", serviceId);
                  const serviceDoc = await firestore
                    .collection('services')
                    .doc(serviceId)
                    .get();
                    
                  if (serviceDoc.exists) {
                    const serviceData = serviceDoc.data();
                    console.log("Service trouvé:", serviceData);
                    
                    // Vérifier si le service appartient bien au praticien sélectionné
                    if (serviceData.practitionerId === urlPractitionerId && serviceData.active !== false) {
                      // Déterminer le mode de consultation par défaut
                      let defaultMode = 'cabinet';
                      if (serviceData.remoteOnly) {
                        defaultMode = 'remote';
                      } else if (serviceData.homeOnly) {
                        defaultMode = 'home';
                      }
                      
                      // Définir le service présélectionné avec le mode approprié
                      setSelectedService({
                        id: serviceId,
                        title: serviceData.title || 'Service sans titre',
                        duration: serviceData.duration || 60,
                        price: serviceData.price || 0,
                        remotePrice: serviceData.remotePrice || serviceData.price,
                        homePrice: serviceData.homePrice || serviceData.price,
                        homeTransportFee: serviceData.homeTransportFee || 0,
                        homeRadius: serviceData.homeRadius || 0,
                        restTime: serviceData.restTime || 0,
                        notes: serviceData.notes || '',
                        canBeRemote: serviceData.canBeRemote || false,
                        remoteOnly: serviceData.remoteOnly || false,
                        canBeAtHome: serviceData.canBeAtHome || false,
                        homeOnly: serviceData.homeOnly || false,
                        consultationMode: defaultMode
                      });
                      
                      console.log("Service présélectionné avec mode:", defaultMode);
                      
                      // Passer à l'étape de sélection de la date
                      // setCurrentStep(3);
                    } else {
                      console.warn('Le service n\'appartient pas au praticien ou n\'est pas actif');
                    }
                  } else {
                    console.warn('Service non trouvé:', serviceId);
                  }
                } catch (error) {
                  console.error('Erreur lors de la récupération du service:', error);
                }
              }
            } else {
              console.warn('Le praticien n\'est pas actif');
            }
          } else {
            console.warn('Praticien non trouvé:', urlPractitionerId);
          }
        } catch (error) {
          console.error('Erreur lors de la récupération du praticien:', error);
        }
      }
    };

    loadFromUrl();
  }, [practitionerId, serviceId, location.search, setSelectedPractitioner, setSelectedService, bookingData.practitioner, setCurrentStep]);

  // Réinitialiser les étapes dépendantes lorsque les étapes précédentes changent
  useEffect(() => {
    // Si le praticien change, réinitialiser les choix qui en dépendent
    if (currentStep >= 2 && !bookingData.practitioner) {
      updateBookingData({
        service: null,
        serviceTitle: null,
        duration: null,
        price: null,
        remotePrice: null,
        homePrice: null,
        homeTransportFee: 0,
        date: null,
        time: null,
        formattedDate: null,
        consultationMode: null,
        isRemote: false,
        isHomeVisit: false
      });
    }
    
    // Si le service change, réinitialiser la date et l'heure
    if (currentStep >= 3 && !bookingData.service) {
      updateBookingData({
        date: null,
        time: null,
        formattedDate: null
      });
    }
    
    // Si la date change, réinitialiser l'heure
    if (currentStep >= 4 && !bookingData.date) {
      updateBookingData({
        time: null
      });
    }
  }, [currentStep, bookingData.practitioner, bookingData.service, bookingData.date, updateBookingData]);

  // Rendre le composant correspondant à l'étape actuelle
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <PractitionerSelection presetPractitionerId={practitionerId} />;
      case 2:
        return <ServiceSelection practitionerId={bookingData.practitioner?.id} presetServiceId={serviceId} />;
      case 3:
        return <Calendar 
                practitionerId={bookingData.practitioner?.id} 
                serviceId={bookingData.service}
                serviceDuration={bookingData.duration}
                onDateSelect={(date) => updateBookingData({date})}
               />;
      case 4:
        return <TimeSlots 
                practitionerId={bookingData.practitioner?.id}
                selectedDate={bookingData.date}
                serviceId={bookingData.service}
                serviceDuration={bookingData.duration}
                onTimeSelect={(time) => updateBookingData({time})}
               />;
      case 5:
        return <ClientForm />;
      case 6:
        return <Confirmation />;
      default:
        return <PractitionerSelection presetPractitionerId={practitionerId} />;
    }
  };

  // Fonction pour obtenir le libellé de l'étape
  const getStepLabel = (step) => {
    switch (step) {
      case 1: return 'Praticien';
      case 2: return 'Service';
      case 3: return 'Date';
      case 4: return 'Heure';
      case 5: return 'Vos informations';
      case 6: return 'Confirmation';
      default: return `Étape ${step}`;
    }
  };

  return (
    <div className="booking-container">
      <div className="booking-main">
        {/* Indicateur de progression */}
        <div className="progress-container">
          <div className="progress-steps">
            {[1, 2, 3, 4, 5, 6].map((step) => (
              <div
                key={step}
                className={`step ${currentStep === step ? 'active' : ''} ${
                  currentStep > step ? 'completed' : ''
                }`}
              >
                <div className="step-circle">{step}</div>
                <div className="step-label">
                  {getStepLabel(step)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Contenu de l'étape actuelle */}
        {renderStep()}
      </div>

      {/* Sidebar avec récapitulatif */}
      <Sidebar />
    </div>
  );
};

export default Booking;